'use client';

import type { ComboboxProps } from '@headlessui/react';
import {
	Combobox,
	ComboboxButton,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from '@headlessui/react';
import LoadingIcon from 'assets/icons/loading-spinner.svg';
import SearchIcon from 'assets/icons/search.svg';
import { clearFocus } from 'helpers/clear-focus';
import {
	type ChangeEventHandler,
	type KeyboardEventHandler,
	type ReactNode,
	type SubmitEventHandler,
	useEffect,
	useRef,
	useState,
} from 'react';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import { Count } from '../count';
import styles from './search-bar-combobox.module.scss';
import type { CollectionSearchSuggestion } from '@metmuseum/types';
import debounce from 'lodash/debounce';

const suggestionFetcher = (url: string) => fetch(url).then((r) => r.json());

// uses headless UI for easy WCAG compliance/a11y features
// see: https://headlessui.com/react/combobox
export const SearchBarCombobox = ({
	buttonBehavior = 'toggle',
	clearOnSelect = true,
	forwardParams,
	handleComboboxSelection,
	icon,
	id,
	initialQuery = '',
	isSearching,
	modal = false,
	mode = 'suggest',
	name = 'q',
	numericOnly = false,
	onClear,
	onClose,
	onSubmit,
	placeholderText,
	suggestionEndpoint,
	suggestionField,
}: Props) => {
	const [query, setQuery] = useState(initialQuery); // controlled component - we need to display the user's keystrokes instantly
	const [debouncedQuery, setDebouncedQuery] = useState(initialQuery); // we use this for the fetch

	// we have to use a ref to keep this persistent between renders
	const updateDebouncedQuery = useRef(null);
	// this is React's blessed pattern for lazy initializing a reference once. == is intentional in the idiom, so it also matches on undefined
	if (updateDebouncedQuery.current == null) {
		updateDebouncedQuery.current = debounce(setDebouncedQuery, 300, {
			leading: true, // important to show suggestions immediately
			maxWait: 600, // important to keep the suggestions fresh as they type
		});
	}

	useEffect(() => {
		updateDebouncedQuery.current(query);
	}, [query]);

	useEffect(() => {
		// this just cleans up the debounce on unmount.
		// it can't be done in the other useEffect because it would cancel every time query changes, defeating the purpose
		return updateDebouncedQuery.current.cancel;
	}, []);

	const hasQuery = !!query?.trim();
	const hasDebouncedQuery = !!debouncedQuery?.trim();

	const params = new URLSearchParams(forwardParams ? forwardParams : {});
	if (suggestionField) {
		params.set('field', suggestionField);
	}
	params.set('text', debouncedQuery);

	// TODO: could make this into like "useSuggestion" hook one day?
	const { data, error, isLoading, isValidating } = useSWR<
		CollectionSearchSuggestion[]
	>(
		// only fetch if there's an endpoint prop and a query
		suggestionEndpoint && debouncedQuery.trim()
			? `${suggestionEndpoint}?${params}`
			: null,
		suggestionFetcher,
		{
			keepPreviousData: hasQuery && hasDebouncedQuery, // if no query, don't show previous results, because that's weird. but also, if query and it's had time to debounce, keep the previous results in state to prevent flashes as the user types
		},
	);

	if (error) {
		// TODO: send to Sentry? Error boundaries?
		console.error(error);
	}

	const hasResults = !!data?.length;
	const hasNoResults = // needs several conditions so that it doesn't flash stale results
		hasQuery && // user has input text
		hasDebouncedQuery && // and that text has been debounced (is now searchable)
		!isValidating && // and we're not currently searching or showing fallback data from keepPreviousData
		data?.length === 0; // and no items

	const classString =
		isSearching || isLoading
			? `${styles.searching} ${styles.wrapper}`
			: styles.wrapper;

	const submitQuery = (rawQuery: string) => {
		const query = rawQuery.trim();

		if (query && onSubmit) {
			onSubmit(query);
		}

		clearFocus();
	};

	const handleInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const next = e.currentTarget.value;

		//This handles the case where a user highlights a selection and hits backspace + when they begin typing a new query
		//The logic here is "when a user begins typing something new, clear the previous query".
		if (next?.length < 2) onClear?.();

		setQuery(next);
	};

	const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
		e.preventDefault(); // prevents hard navigation

		// I feel like FormData is somewhat less brittle than keeping track of the input value separately
		const data = new FormData(e.currentTarget);
		// coerce to string to make TypeScript happy that value is not a File (alas, a humble string)
		const query = String(data.get(name) ?? '').trim();

		submitQuery(query);
	};

	const handleInputKeyDownCapture: KeyboardEventHandler<HTMLInputElement> = (
		e,
	) => {
		if (buttonBehavior !== 'submit') {
			return;
		}

		if (e.key !== 'Enter') {
			return;
		}

		if (suggestionEndpoint && data?.length) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();
		submitQuery(query);
	};

	const handleSuggestionClose = () => {
		onClose?.(query);
	};

	const handleComboboxChange = (value: CollectionSearchSuggestion) => {
		if (!value) return;
		handleComboboxSelection?.(value);
		if (value.field !== 'q') {
			// for example: select "apply a filter" in the main search bar, but you also had a search query in it, we make sure the search bar resets to your initialQuery, not a blank bar
			clearOnSelect && setQuery(initialQuery);
		}
	};

	return (
		<form
			aria-busy={isSearching || isLoading}
			className={classString}
			onSubmit={handleSubmit}
			role="search"
			data-testid={'search-bar-combobox'}
			autoComplete="off" // disable the browser autocomplete so it doesn't conflict with the combobox
		>
			{!!icon && <div className={styles.iconContainer}>{icon}</div>}
			<Combobox onChange={handleComboboxChange} onClose={handleSuggestionClose}>
				<ComboboxInput
					className={styles.input}
					id={id}
					inputMode={numericOnly ? 'numeric' : 'search'}
					name={name}
					onChange={handleInputChange}
					onKeyDownCapture={handleInputKeyDownCapture}
					pattern={numericOnly ? '[0-9]*' : '.*'}
					placeholder={placeholderText || 'Search'}
					type={numericOnly ? 'number' : 'search'}
					value={query}
					autoComplete="off" // safari needs this on the input itself, not just the parent form
				/>

				<ComboboxButton
					aria-label="Search Submit"
					className={styles.submitButton}
					disabled={isSearching} // besides preventing double clicks, this is a helpful selector for e2e tests
					type={buttonBehavior === 'submit' ? 'submit' : undefined}
				>
					{isSearching || isLoading ? <LoadingIcon /> : <SearchIcon />}
				</ComboboxButton>

				{mode === 'commandPalette' && hasQuery && (
					<ComboboxOptions
						modal={modal}
						anchor={{
							to: 'bottom end',
							gap: '6px',
							offset: 'var(--button-width)',
						}}
						className={styles.commandPaletteContainer}
					>
						<ComboboxOption
							className={styles.suggestion}
							key={query}
							value={{ field: 'q', value: query }}
						>
							Search for{' '}
							<Highlighter
								autoEscape={true}
								searchWords={query.split(' ')}
								textToHighlight={query}
								highlightTag="em"
							/>
						</ComboboxOption>
						{hasResults && (
							<>
								<hr className={styles.rule} />
								<span className={styles.helperText}>Apply a filter</span>
								{data.map(({ id, friendlyField, value, field }) => (
									<ComboboxOption
										className={styles.suggestion}
										key={`${id}-${field}`}
										value={{
											field,
											value,
										}}
									>
										<span className={styles.filterChip}>
											<Highlighter
												autoEscape={true}
												searchWords={query.split(' ')}
												textToHighlight={value}
												highlightTag="em"
											/>
										</span>{' '}
										<span className={styles.suggestionCategoryLabel}>
											in {friendlyField}
										</span>
									</ComboboxOption>
								))}
							</>
						)}
					</ComboboxOptions>
				)}

				{mode === 'suggest' && (
					<ComboboxOptions
						modal={modal}
						anchor={{
							to: 'bottom start',
							gap: '6px',
							padding: '48px 0px', // breathing room around the edges of the viewport for floating UI
						}}
						className={styles.suggestionContainer}
					>
						{hasResults &&
							data?.map((option) => (
								<ComboboxOption
									className={styles.suggestion}
									key={option.id}
									value={option}
								>
									<Highlighter
										autoEscape={true}
										searchWords={query.split(' ')}
										textToHighlight={option.value}
										highlightTag="em"
									/>{' '}
									<Count count={option.doc_count} />
								</ComboboxOption>
							))}
						{hasNoResults && (
							<ComboboxOption value={query}>
								<div className={styles.noResults}>
									No results for &ldquo;{query}&rdquo;
								</div>
							</ComboboxOption>
						)}
					</ComboboxOptions>
				)}
			</Combobox>
		</form>
	);
};

interface Props {
	buttonBehavior?: 'submit' | 'toggle';
	clearOnSelect?: boolean;
	forwardParams?: string;
	// TODO: in the future, we should make this a generic so it can handle any data, but for now it's typed to Suggestions
	handleComboboxSelection?: ComboboxProps<
		CollectionSearchSuggestion,
		false
	>['onChange'];
	icon?: ReactNode;
	id: string;
	initialQuery?: string;
	isSearching?: boolean;
	/** @remarks be careful with this. it locks scrolling on the entire html element, and causes issues when used inside scrollable containers like left rail */
	modal?: boolean;
	mode?: 'commandPalette' | 'suggest';
	name?: string;
	numericOnly?: boolean;
	onClear?: () => void;
	onClose?: (query: string) => void;
	onSubmit?: (query: string) => void;
	placeholderText?: string;
	suggestionEndpoint: string;
	suggestionField?: string;
}
